// The page in front of the whole site for a learner who signed in with a
// password somebody else chose — staff who enrolled them or reset it, or the
// MyMentor operator from the command line. Whoever handed it over knows it, and
// it travelled in a messenger; until the learner replaces it the server answers
// every other request with 403 password_change_required, so there is nothing
// else to show.
//
// The page also says where the password chosen here will work. One this
// platform's staff issued is bound to this platform, and so is the one chosen
// from it: the staff who issued it could have done the choosing. The page
// cannot tell which kind this is (the server does not say), so it states the
// rule for the case it applies to, and the only way past it — the MyMentor
// operator issuing a new temporary password, one that works on every platform.
//
// The issued password is counted like a sign-in: wrong ones add to the
// account's tally, and while the account is locked it is not looked at. A lock
// is counted down here the way the sign-in page counts it (lib/lock.ts).
//
// App renders this instead of the shell and leaves the address alone, so once
// the password is chosen the site carries on to where the learner was going.
//
// The rules are the server's (internal/secret: 8 to 200 characters, not the
// password already in force), repeated so the form can answer at once; the
// server still decides, and its refusals are put under the input they are about.

import { useEffect, useId, useRef, useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError, api } from '../api'
import { useAuth } from '../auth'
import { useT } from '../i18n'
import auth from '../i18n/auth'
import nav from '../i18n/nav'
import { clock, lockOf, useLockClock } from '../lib/lock'
import { SignedOutFrame, messageOf } from '../lib/ui'
import { AuthBrand, PasswordField } from './SignIn'
import '../styles/auth.css'

/** internal/secret MinLen and MaxLen, counted in characters as the server counts them. */
const PASSWORD_MIN = 8
const PASSWORD_MAX = 200

type FieldName = 'current_password' | 'new_password' | 'again'
type Errors = Partial<Record<FieldName, string>>

export function ChoosePassword() {
  const t = useT(auth)
  const tn = useT(nav)
  const { user, signOut, refresh, passwordChanged } = useAuth()
  const id = useId()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [errors, setErrors] = useState<Errors>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { lock, setLock, remaining, lockedFor, counting } = useLockClock()

  const inputs = {
    current_password: useRef<HTMLInputElement | null>(null),
    new_password: useRef<HTMLInputElement | null>(null),
    again: useRef<HTMLInputElement | null>(null),
  }

  // The caret starts in the first box: the password they have just typed at
  // the door is the one it asks for.
  useEffect(() => {
    inputs.current_password.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The inputs are disabled while the request runs, and a disabled input takes
  // no focus; so the input to go back to is noted, and focused once they are
  // enabled again.
  const refocus = useRef<FieldName | null>(null)
  useEffect(() => {
    if (busy || !refocus.current) return
    const which = refocus.current
    refocus.current = null
    inputs[which].current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, errors])

  /** What the form can decide without asking; every complaint at once. */
  function check(): Errors {
    const found: Errors = {}
    if (!current) found.current_password = t('enterIssuedPassword')

    const length = count(next)
    if (!next) found.new_password = t('enterNewPassword')
    else if (length < PASSWORD_MIN || length > PASSWORD_MAX) {
      found.new_password = t('newPasswordLength', { min: PASSWORD_MIN, max: PASSWORD_MAX })
    } else if (next === current) found.new_password = t('newSameAsIssued')

    if (!again) found.again = t('repeatNewPassword')
    else if (again !== next) found.again = t('passwordsDiffer')
    return found
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return

    const found = check()
    setErrors(found)
    setNotice(null)
    const first = firstOf(found)
    if (first) {
      inputs[first].current?.focus()
      return
    }

    setBusy(true)
    try {
      // `quiet`: a wrong current password is answered 401, and that 401 is
      // about the box in front of the learner, not about their session.
      // Neither password is trimmed — a space is part of a password.
      await api<void>('/auth/password', {
        method: 'POST',
        body: { current_password: current, new_password: next },
        quiet: true,
      })
      // Lifts the page at once; the site takes over from here.
      passwordChanged().catch(() => undefined)
    } catch (err) {
      await explain(err)
      setBusy(false)
    }
  }

  /**
   * Whether the password still has to be chosen, asked of the server afresh.
   *
   * A refused issued password has two readings. Either it was mistyped, or it
   * is no longer the password in force because it has already been replaced —
   * in another tab of this browser, which shares this session and so is not
   * signed out by the change. The second is no mistake of anybody's: once
   * /auth/me says the flag is gone the provider takes this page away and the
   * site opens, and nothing needs saying. If the question itself fails, the
   * refusal is all there is to go on.
   */
  async function stillToChoose(): Promise<boolean> {
    try {
      const now = await refresh({ fresh: true })
      return now.must_change_password === true
    } catch {
      return true
    }
  }

  /** Puts a refusal where it belongs: under an input, above the button, on the clock, or out of the door. */
  async function explain(err: unknown) {
    // Locked (423): nothing was compared, so neither box is marked and nothing
    // typed is thrown away. The password may already have been chosen in
    // another tab, though, and then there is no lock to wait for here.
    const locked = lockOf(err)
    if (locked) {
      if (!(await stillToChoose())) return
      setLock(locked)
      return
    }
    // Any other answer is newer than a lock on screen.
    setLock(null)

    if (err instanceof ApiError && err.isUnauthenticated) {
      if (err.code === 'bad_credentials') {
        if (!(await stillToChoose())) return
        setErrors({ current_password: t('wrongIssuedPassword') })
        // Only the wrong one is emptied; the new password was fine.
        setCurrent('')
        refocus.current = 'current_password'
        return
      }
      // Any other 401 is the session itself, which `quiet` kept to this form;
      // signing out puts the learner back at the door, told why when it was
      // a sign-in on another device.
      await signOut(err)
      return
    }

    if (err instanceof ApiError && err.status === 422 && err.fields['new_password'] !== undefined) {
      // Two rules turn a new password away — its length, and its being the
      // password already in force. Which one it was is read from the length
      // rule both sides hold, not from the server's English.
      const length = count(next)
      setErrors({
        new_password:
          length < PASSWORD_MIN || length > PASSWORD_MAX
            ? t('newPasswordLength', { min: PASSWORD_MIN, max: PASSWORD_MAX })
            : t('newSameAsIssued'),
      })
      refocus.current = 'new_password'
      return
    }

    // Everything else — too many requests, the server unreachable — belongs
    // to no single input.
    setNotice(messageOf(err, auth))
  }

  const who = user ? user.full_name.trim() || user.phone : ''

  return (
    <SignedOutFrame>
      <div className="login auth">
        <main className="login__card choose-password" aria-labelledby={`${id}-title`}>
          <AuthBrand />
          <h1 className="auth__title" id={`${id}-title`}>
            {t('choosePasswordTitle')}
          </h1>
          <p className="auth__lead">{t('choosePasswordLead')}</p>
          {who && <p className="choose-password__who">{tn('signedInAs', { name: who })}</p>}

          <form onSubmit={submit} noValidate>
            {/* For a password manager: the account the new password belongs to. */}
            <input
              type="text"
              className="visually-hidden"
              autoComplete="username"
              value={user?.phone ?? ''}
              readOnly
              tabIndex={-1}
              aria-hidden="true"
            />

            <PasswordField
              id={`${id}-current`}
              label={t('issuedPasswordLabel')}
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              error={errors.current_password}
              hint={t('issuedPasswordHint')}
              inputRef={inputs.current_password}
              disabled={busy}
            />
            <PasswordField
              id={`${id}-new`}
              label={t('newPasswordLabel')}
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              error={errors.new_password}
              inputRef={inputs.new_password}
              describedBy={`${id}-rules`}
              disabled={busy}
            />

            <div className="choose-password__rules" id={`${id}-rules`}>
              <strong>{t('rulesTitle')}</strong>
              <ul>
                <li>{t('ruleLength', { min: PASSWORD_MIN, max: PASSWORD_MAX })}</li>
                <li>{t('ruleDifferent')}</li>
                <li>{t('ruleYours')}</li>
              </ul>
            </div>

            <PasswordField
              id={`${id}-again`}
              label={t('repeatNewPasswordLabel')}
              value={again}
              onChange={setAgain}
              autoComplete="new-password"
              error={errors.again}
              inputRef={inputs.again}
              disabled={busy}
            />

            {lock && (
              <div className="auth__alert auth__alert--lock" role="alert">
                {counting ? (
                  <p>
                    <span className="visually-hidden">{t('lockedCountdown', { time: clock(lockedFor) })}</span>
                    <span aria-hidden="true">{t('lockedCountdown', { time: clock(remaining) })}</span>
                  </p>
                ) : (
                  <p>{t('lockedLater')}</p>
                )}
              </div>
            )}
            {!lock && notice && (
              <p className="auth__alert" role="alert">
                {notice}
              </p>
            )}

            <p className="choose-password__devices">{t('choosePasswordDevices')}</p>
            <p className="choose-password__devices">{t('choosePasswordScope')}</p>

            <button type="submit" className="button button--wide" disabled={busy}>
              {busy ? t('savingPassword') : t('savePassword')}
            </button>
          </form>

          <div className="choose-password__later">
            <p>{t('laterHint')}</p>
            <button type="button" className="link" onClick={() => void signOut()} disabled={busy}>
              {tn('signOut')}
            </button>
          </div>
        </main>
      </div>
    </SignedOutFrame>
  )
}

/** The first input, top to bottom, that has something to say. */
function firstOf(e: Errors): FieldName | null {
  if (e.current_password) return 'current_password'
  if (e.new_password) return 'new_password'
  if (e.again) return 'again'
  return null
}

/** Characters, as the server counts them — not UTF-16 units. */
function count(s: string): number {
  return [...s].length
}
